import { Author } from '@blockworks/platform/api/strapi';
import { AvatarGroup, FlexBox, Link, Text } from '@blockworks/ui/components';
import { DateFormat } from '@blockworks/ui/date-time';

import SingleByline from '@/components/byline/single-byline';

interface BylineProps {
    authors: Author[];
    publishedAt: string;
    shouldLinkToSocial?: boolean;
}

const Byline = ({ authors, publishedAt, shouldLinkToSocial }: BylineProps) => {
    const [primaryAuthor, secondaryAuthor, ...additional] = authors.filter(Boolean);
    const isMultiByline = primaryAuthor && secondaryAuthor;
    if (!primaryAuthor) return null;

    if (!isMultiByline) {
        return (
            <SingleByline
                name={primaryAuthor?.name!}
                img={primaryAuthor?.image?.url!}
                publishedAt={{ date: publishedAt, isEpoch: false }}
                href={
                    shouldLinkToSocial
                        ? primaryAuthor.twitterUrl || primaryAuthor.linkedinUrl || ''
                        : `/team/${primaryAuthor?.slug}`
                }
                isExternalLink={shouldLinkToSocial ? true : false}
            />
        );
    }

    const additionalAuthorNames = `and ${additional.length} others`;

    return (
        <FlexBox mb={3} alignItems="center" gap="md">
            <AvatarGroup
                stack="asc"
                size={32}
                avatars={authors?.map(author => ({
                    src: author.image?.url!,
                    alt: author.name!,
                    href: shouldLinkToSocial ? author.twitterUrl || author.linkedinUrl || '' : `/team/${author?.slug}`,
                    isExternalLink: shouldLinkToSocial ? true : false,
                }))}
                hasTooltip
            />
            <FlexBox col>
                <Text size="xs" color="deselect" weight="regular">
                    <Link fontWeight="semibold" href={`/team/${primaryAuthor.slug}`}>
                        {primaryAuthor.name}
                    </Link>
                    {additional.length === 0 ? ' and ' : ', '}
                    <Link
                        fontWeight="semibold"
                        target={shouldLinkToSocial ? '_blank' : '_self'}
                        href={
                            shouldLinkToSocial
                                ? secondaryAuthor.twitterUrl || secondaryAuthor.linkedinUrl || ''
                                : `/team/${secondaryAuthor?.slug}`
                        }
                    >
                        {secondaryAuthor.name}
                    </Link>
                    {additional.length > 0 && <Text as="span"> {additionalAuthorNames}</Text>}
                </Text>
                <Text size="xs">
                    <DateFormat date={publishedAt} />
                </Text>
            </FlexBox>
        </FlexBox>
    );
};

export default Byline;
